import _ from "lodash";

import { useCallback, useEffect, useState } from "react";
import { FVIEquipmentItem, FVIPipingItem, FVIStructureItem } from "../utils/models";
import { getSomeChecklistItemsMissing, matchPhrases } from "../utils/utilities";
import { getMappedAnomalies } from "./useMappedAnomalies";

import config from "../utils/config";
import usePreviousList from "./usePreviousList";

interface IncompleteInspectedSections {
    incompleteSections: string[];
    isLastInspectedItemComplete: boolean;
}


interface InspectedItem {
    id: FVIPipingItem['id'];
    inspectionType?: string;
    hasPID?: FVIPipingItem['hasPID'];
    hasAnomalies: FVIPipingItem['hasAnomalies'];
    pIDImages?: FVIPipingItem['pIDImages'];
    anomalyItems: FVIPipingItem['anomalyItems'];
    anomalyLocations?: FVIPipingItem['anomalyLocations'];
    generalDrawing?: FVIStructureItem['generalDrawing'];
    generalPhotos: FVIPipingItem['generalPhotos'];
    anomalySummary?: FVIPipingItem['anomalySummary'] | FVIEquipmentItem['anomalySummary'];
    checklistItems?: FVIPipingItem['checklistItems'];
    pdoChecklist?: FVIPipingItem['pdoChecklist'];
}

const SECTION_TITLES = {
    ANOMALY_SUMMARY: 'Anomaly Summary & Description',
    ANOMALY_ITEMS: 'Anomaly Items / General Photos',
    PID: 'P & ID',
    CHECKLIST_ITEMS: 'Checklist Items',
    GENERAL_DRAWING: 'General Drawing',
    PDO_DETAILS: 'Potential Drop Object (Checklist)',
}

const useIncompleteInspectedSections = (inspectedItems: InspectedItem[]): IncompleteInspectedSections => {
    const [incompleteSections, setIncompleteSections] = useState<string[]>([]);

    const checkPDOIsCompleted = useCallback((inspectedItem: InspectedItem) => {
        if (inspectedItem.pdoChecklist) {
            const isChecklistCompleted = inspectedItem.pdoChecklist.every((checklist) => checklist.value);
            
            if (!isChecklistCompleted) {
                return false;
            }
        }

        const hasPDO = matchPhrases(_.get(inspectedItem, 'hasPDO'), 'yes');

        if (hasPDO) {
            return inspectedItem.anomalyItems.every((item) => {
                const pdoDetails = _.pick(item, ['pdoLocation', 'pdoDescription', 'weight', 'height', 'threat']);
                const isPDORequired = matchPhrases(_.get(item, 'hasPDO'), 'yes');

                return isPDORequired ? Object.values(pdoDetails).every((value) => value) : true;
            });
        }

        return true;
    }, []);

    const checkPIDIsCompleted = useCallback((inspectedItem: InspectedItem) => {
        if (matchPhrases(inspectedItem.hasPID, 'yes')) {
            if (inspectedItem.pIDImages) {
                return inspectedItem.pIDImages.length > 0;
            }
        }

        return true;
    }, []);

    const checkAnomalyCompleted = useCallback((inspectedItem: InspectedItem) => {
        const hasAnomalyLocations = inspectedItem.anomalyLocations ? inspectedItem.anomalyLocations.length > 0 : true;

        if (matchPhrases(inspectedItem.hasAnomalies, 'yes')) {
            return inspectedItem.anomalyItems.length > 0 && hasAnomalyLocations;
        }

        return hasAnomalyLocations && inspectedItem.generalPhotos.length > 0
    }, []);

    const checkGeneralDrawingCompleted = useCallback((inspectedItem: InspectedItem) => {
        if (matchPhrases(inspectedItem.hasAnomalies, 'no') && inspectedItem.generalDrawing) {
            return inspectedItem.generalDrawing.length > 0;
        }

        return true;
    }, []);

    const checkAnomalySumary = useCallback((inspectedItem: InspectedItem) => {
        if (inspectedItem.anomalySummary) {
            return Object.values(inspectedItem.anomalySummary).every((value) => !['', undefined, null].includes(value as any));
        }

        return true;
    }, []);

    const removeSectionFromIncomplete = useCallback((sectionName: string) => {
        if (!incompleteSections.includes(sectionName)) return;

        const newIncompleteSections = incompleteSections.filter((section) => section !== sectionName);
        setIncompleteSections(newIncompleteSections);
    }, [incompleteSections])

    const addSectionToIncomplete = useCallback((sectionName: string) => {
        setIncompleteSections((previous) => [...previous, sectionName]);
    }, []);

    const checkChecklistIsRequired = useCallback((inspectedItem: InspectedItem) => {
        return inspectedItem.checklistItems
    }, []);

    const previousList = usePreviousList(inspectedItems);

    useEffect(() => {
		if (config.enableReportCompletionBeforePublish) {
            if (_.isEqual(previousList, inspectedItems)) return;

            let lastPipingItem = inspectedItems.at(-1);
            if (lastPipingItem) {
                if (checkAnomalySumary(lastPipingItem)) removeSectionFromIncomplete(SECTION_TITLES.ANOMALY_SUMMARY);
                else addSectionToIncomplete(SECTION_TITLES.ANOMALY_SUMMARY);

                if (checkPDOIsCompleted(lastPipingItem)) removeSectionFromIncomplete(SECTION_TITLES.PDO_DETAILS);
                else addSectionToIncomplete(SECTION_TITLES.PDO_DETAILS);

                if (checkAnomalyCompleted(lastPipingItem)) removeSectionFromIncomplete(SECTION_TITLES.ANOMALY_ITEMS);
                else addSectionToIncomplete(SECTION_TITLES.ANOMALY_ITEMS);

                if (checkGeneralDrawingCompleted(lastPipingItem)) removeSectionFromIncomplete(SECTION_TITLES.GENERAL_DRAWING);
                else addSectionToIncomplete(SECTION_TITLES.GENERAL_DRAWING);

                if (lastPipingItem.pIDImages) {
                    if (checkPIDIsCompleted(lastPipingItem)) removeSectionFromIncomplete(SECTION_TITLES.PID);
                    else addSectionToIncomplete(SECTION_TITLES.PID);
                }
                
                if (lastPipingItem.checklistItems) {
                    const itemAnomalies = getMappedAnomalies(lastPipingItem);

                    if (checkChecklistIsRequired(lastPipingItem) && getSomeChecklistItemsMissing(lastPipingItem.checklistItems, itemAnomalies)) {
                        return addSectionToIncomplete(SECTION_TITLES.CHECKLIST_ITEMS);
                    }
                    
                    removeSectionFromIncomplete(SECTION_TITLES.CHECKLIST_ITEMS);
                }
            } else {
                setIncompleteSections([]);
            }
        }
    }, [inspectedItems, previousList, addSectionToIncomplete, removeSectionFromIncomplete, checkChecklistIsRequired, checkPDOIsCompleted, checkAnomalyCompleted, checkAnomalySumary, checkPIDIsCompleted, checkGeneralDrawingCompleted]);
    
    const uniqueIncompleteSections = _.uniq(incompleteSections)
    return { incompleteSections: uniqueIncompleteSections, isLastInspectedItemComplete: uniqueIncompleteSections.length === 0 }
};

export default useIncompleteInspectedSections;